import { DEBUG } from '@src/DEBUG';
DEBUG.open('/app/app.module.ts');

import { HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, Injector, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule, Routes } from '@angular/router';
import { VasatService } from 'vasat-ng';
import { NotFoundComponent, RootComponent } from './root.component';

import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';

import { FourDService } from '4d-mapper';
import { MatIconRegistry, MatPaginatorIntl } from '@angular/material';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { Vasat, VasatConfig } from 'vasat';
import { AppService } from './commons/app.service';
import { MatPaginatorIntlLang } from './commons/date-pipe';
import { AppRoutes } from './commons/global';
import { AccountNeeded, LoginNeeded, SharedModule } from './commons/shared.module';

import { SnotifyModule, SnotifyService } from 'ng-snotify';
import 'rxjs-compat';
import { SnotifyConfig } from './libs/snotify.config';

import { environment } from '../environments/environment';
import { FourDInjector } from './commons/FourDInjector';

import { ResizableModule } from 'angular-resizable-element';
import { ColorPickerModule } from 'ngx-color-picker';
import { NgxStripeModule } from 'ngx-stripe';
import { FileDataService } from './v2/commons/file-data.service';
import { StreamDataService } from './v2/commons/stream-data.service';
import { XsIdentityInterface } from './xs/interfaces/identify.interface';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fab } from '@fortawesome/free-brands-svg-icons';
import { fal } from '@fortawesome/pro-light-svg-icons';
import { far } from '@fortawesome/pro-regular-svg-icons';
import { fas } from '@fortawesome/pro-solid-svg-icons';
library.add(fas, far, fal, fab);

import { XsModule } from './xs/xs.module';

/***********************************************
 *  Main module Routes configuration
 ***********************************************/
export const routes: Routes = [
  { path: '', redirectTo: AppRoutes.MAIN, pathMatch: 'full' },

  // direct /map/[n] reference, like a direct view-link
  { path: AppRoutes.MAP, loadChildren: () => import('./main/map/index').then((m) => m.MapModule) },

  // authentication and user control
  { path: AppRoutes.AUTH, loadChildren: () => import('./auth/index').then((m) => m.AuthModule) },

  // main app, normal operation
  { path: AppRoutes.MAIN, loadChildren: () => import('./main/index').then((m) => m.MainModule) },

  { path: AppRoutes.INIT, loadChildren: () => import('./init/init.module').then((m) => m.InitModule) },

  { path: 'v2', loadChildren: () => import('./v2/index').then((m) => m.Ver2Module) },
  { path: 'v5', loadChildren: () => import('./v5/index').then((m) => m.Ver5Module) },

  // {path: 'maker', loadChildren: () => import('./maker/index').then(m => m.MakerModule)},

  { path: '**', component: NotFoundComponent },
];

// window['process'] = {env: {NODE_ENV: environment.production ? 'production' : 'dev'}}
// console.log ( '############', window );

export const AppRouting = RouterModule.forRoot(routes);

// console.log ( 'app.module.ts :: routes', AppRouting, routes ) ;

/***********************************************
 *  Translation configuration
 ***********************************************/
export function HttpLoaderFactory(http: HttpClient) {
  DEBUG.log('/app/app.module.ts', 'HttpLoaderFactory', http);
  return new TranslateHttpLoader(http);
}

/***********************************************
 *  Load vasat instance paramaters from server
 ***********************************************/
export function loadVasatInstance(service: AppService) {
  DEBUG.log('/app/app.module.ts', 'loadVasatInstance', service);
  return () => service.loadInstanceConfig().subscribe();
}
/*
export function initGapi(gapiSession: GapiSession) {
  return () => gapiSession.initClient();
}*/

var vasat = new Vasat(
  new VasatConfig(
    environment.production
      ? location.protocol + '//' + location.host + (environment.embedded_vasat ? '' : '/vasat')
      : // extra to now have isntance deployed on sandpit to point to live server by default.
        // location.host == 'sandpit.4dmapper.com' ? 'https://beta.4dmapper.com/vasat' :

        // 4dm environments
        //'https://app.4dmapper.com'

    
    //'https://bmsurvey.mapsender.com/vasat'
    //'https://map.mapsender.com'
    'http://geoshare.geoxpert.com'
    // 'https://app.4dlink.com' 
    // local development
    //    'http://localhost:9000'

    // around the world
    //'https://cemeteries.ruapehudc.govt.nz/vasat'
    //'https://www.4dlink.geolinkjapan.com'
    // 'https://4dcan-jp.4dmapper.com/'
    // 'https://imagery.outlineglobal.com.au'
    ,
    localStorage['last4d_token'] || '4dmapper',
    localStorage['last4d_secret'] || ''
  ),
  null
);

DEBUG.log('/app/app.module.ts', 'Vasat', vasat);

@NgModule({
  imports: [
    BrowserAnimationsModule,
    BrowserModule,
    HttpClientModule,
    FontAwesomeModule,
    HttpClientModule,
    AppRouting,
    // DndModule.forRoot(), -> disabled, if we really don't need it, remove from package.json
    SnotifyModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    SharedModule,
    XsModule,
    NgxStripeModule.forRoot(),
    ResizableModule,
    NgSelectModule,
    FormsModule,
    ColorPickerModule,
  ],
  declarations: [RootComponent],
  providers: [
    AppService,
    {
      provide: APP_INITIALIZER,
      useFactory: loadVasatInstance,
      multi: true,
      deps: [AppService],
    },

    {
      provide: MatPaginatorIntl,
      useClass: MatPaginatorIntlLang,
    },

    {
      provide: 'SnotifyToastConfig',
      useValue: SnotifyConfig,
    },
    SnotifyService,
    LoginNeeded,
    AccountNeeded,
    {
      provide: FourDService,
      useValue: new FourDService(vasat),
    },
    {
      provide: Vasat,
      useValue: vasat,
    },
    {
      provide: VasatService,
      useValue: vasat,
    },
    FileDataService,
    StreamDataService,
  ],

  bootstrap: [RootComponent],
})
export class AppModule implements XsIdentityInterface {
  __ID = null;

  static injector: Injector = null;
  f: FourDInjector = null;

  constructor(public matIconRegistry: MatIconRegistry, injector: Injector) {
    this.__ID = XsIdentityInterface.__init(this);

    matIconRegistry.registerFontClassAlias('fontawesome', 'fa');
    AppModule.injector = injector;
    this.f = new FourDInjector(injector);

    DEBUG.log('/app/app.module.ts', 'CONSTRUCTOR', [injector, this.f]);
  }
}
